import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import { roleLabel } from "@/lib/roleLabels";
import type { Campaign, Paginated, Role } from "@/lib/types";

type Props = { onClose: () => void };

export function InviteMemberModal({ onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const roles = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await api.get<Role[]>(ApiPaths.orgRoles);
      return res.data;
    },
  });

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selected, setSelected] = useState<string[]>(["inspector"]);
  const [campaignIds, setCampaignIds] = useState<string[]>([]);
  const [issuedUrl, setIssuedUrl] = useState<string | null>(null);

  // Operators (inspector role) can be pre-assigned campaigns at invite
  // time — applied when they accept (OPERATOR_OPS #4).
  const isOperator = selected.includes("inspector");
  const campaigns = useQuery({
    queryKey: ["invite-campaigns-roster"],
    queryFn: async () =>
      (
        await api.get<Paginated<Campaign>>(ApiPaths.campaigns, {
          params: { page_size: "200" },
        })
      ).data.results ?? [],
    enabled: isOperator,
    staleTime: 60_000,
  });

  const issue = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ url: string; token: string }>(ApiPaths.orgInvites, {
        email: email.trim(),
        role_slugs: selected,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        ...(isOperator && campaignIds.length > 0
          ? { pending_campaign_ids: campaignIds }
          : {}),
      });
      return res.data;
    },
    onSuccess: (r) => {
      setIssuedUrl(r.url);
      void qc.invalidateQueries({ queryKey: ["org-invites"] });
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("ui.inviteMemberModal.title")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel w-full max-w-md space-y-4">
        <h2 className="text-lg font-semibold">{t("ui.inviteMemberModal.title")}</h2>

        {issuedUrl ? (
          <>
            <p className="text-sm text-nb-text">
              {t("ui.inviteMemberModal.linkGenerated")}
            </p>
            <div className="flex gap-2">
              <input className="input" readOnly value={issuedUrl} />
              <button
                className="btn-primary"
                onClick={() => navigator.clipboard?.writeText(issuedUrl)}
              >
                {t("ui.inviteMemberModal.copy")}
              </button>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-ghost" onClick={onClose}>{t("ui.inviteMemberModal.done")}</button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label" htmlFor="invite-first-name">
                  {t("ui.inviteMemberModal.firstName")} <span aria-hidden className="text-cvp-danger">*</span>
                </label>
                <input
                  id="invite-first-name"
                  type="text"
                  autoComplete="given-name"
                  className="input"
                  required
                  aria-required="true"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="invite-last-name">
                  {t("ui.inviteMemberModal.lastName")} <span aria-hidden className="text-cvp-danger">*</span>
                </label>
                <input
                  id="invite-last-name"
                  type="text"
                  autoComplete="family-name"
                  className="input"
                  required
                  aria-required="true"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="invite-email">
                {t("ui.inviteMemberModal.email")} <span aria-hidden className="text-cvp-danger">*</span>
              </label>
              <input
                id="invite-email"
                type="email"
                className="input"
                required
                aria-required="true"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <span className="label">{t("ui.inviteMemberModal.roles")}</span>
              <div className="flex flex-wrap gap-2">
                {(roles.data ?? []).map((r) => {
                  const on = selected.includes(r.slug);
                  return (
                    <button
                      key={r.slug}
                      type="button"
                      className={`rounded-full border px-3 py-1 text-xs ${on
                          ? "border-nb-mint bg-nb-mint/20 text-nb-mint"
                          : "border-nb-border text-nb-text hover:bg-nb-surface"}`}
                      onClick={() =>
                        setSelected((prev) =>
                          on ? prev.filter((s) => s !== r.slug) : [...prev, r.slug],
                        )
                      }
                    >
                      {roleLabel(t, r.slug, r.name)}
                    </button>
                  );
                })}
              </div>
            </div>

            {isOperator && (campaigns.data ?? []).length > 0 && (
              <div>
                <span className="label">
                  {t("ui.inviteMemberModal.campaigns", "Assign campaigns")}
                </span>
                <p className="mb-1 text-[11px] text-nb-text-3">
                  {t(
                    "ui.inviteMemberModal.campaignsHint",
                    "Optional — applied when the operator accepts. Their schedule can be set afterwards.",
                  )}
                </p>
                <div className="flex max-h-32 flex-wrap gap-2 overflow-y-auto">
                  {(campaigns.data ?? []).map((c) => {
                    const on = campaignIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`rounded-full border px-3 py-1 text-xs ${
                          on
                            ? "border-nb-mint bg-nb-mint/20 text-nb-mint"
                            : "border-nb-border text-nb-text hover:bg-nb-surface"
                        }`}
                        onClick={() =>
                          setCampaignIds((prev) =>
                            on
                              ? prev.filter((x) => x !== c.id)
                              : [...prev, c.id],
                          )
                        }
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {issue.error && (
              <p role="alert" className="text-sm text-cvp-danger">
                {t("ui.inviteMemberModal.errorGeneric")}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-ghost" onClick={onClose}>{t("ui.inviteMemberModal.cancel")}</button>
              <button
                className="btn-primary"
                disabled={
                  !firstName.trim() ||
                  !lastName.trim() ||
                  !email.trim() ||
                  selected.length === 0 ||
                  issue.isPending
                }
                onClick={() => issue.mutate()}
              >
                {issue.isPending ? t("ui.inviteMemberModal.issuing") : t("ui.inviteMemberModal.issueBtn")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}